import { useTranslation } from "react-i18next";
import { ButtonVariant } from "../../types/constants";
import Button from "../ui/Button";
import NewsCard from "../NewsCard";
import { newsServiceFactory } from "../../services/NewsServiceImpl";
import { useInfiniteQuery } from "@tanstack/react-query";
import LoadingPanel from "../../custom-components/CustomLoader";
import { useMemo } from "react";
import { newsPageSize } from "../../Config";
import { INewsFlat } from "../../domain/models/News";

const newsService = newsServiceFactory();
const NEWS_PAGE_SIZE = newsPageSize;

export default function NewsListSection() {
    const { t, i18n } = useTranslation();
    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading,
        isError,
    } = useInfiniteQuery({
        queryKey: ["news", "list", i18n.language],
        queryFn: async ({ pageParam = 1 }) => {
            const result = await newsService.getAllNews(pageParam, NEWS_PAGE_SIZE);
            return result;
        },
        getNextPageParam: (lastPage, allPages) => {
            if (lastPage.totalPages > allPages.length) {
                return allPages.length + 1;
            }
            return undefined;
        },
        initialPageParam: 1,
    });

    const allNews = useMemo<INewsFlat[]>(() => {
        return data?.pages.flatMap((page) => page.rows) ?? [];
    }, [data]);

    const handleLoadMore = () => {
        if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    };

    if (isLoading) {
        return <LoadingPanel isVisible={isLoading} />;
    }

    if (isError) {
        return (
            <section className="w-full max-w-[1280px] mx-auto pb-[100px] flex flex-col gap-[50px]">
                <div className="text-center text-text-300">
                    <p>{t("news.loadError")}</p>
                </div>
            </section>
        );
    }

    return (
        <section className="w-full max-w-[1280px] mx-auto pb-[100px] flex flex-col gap-[50px]">
            {allNews.length > 0 ? (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lap:grid-cols-3 gap-x-5 gap-y-[50px]">
                        {allNews.map((item) => (
                            <NewsCard key={item.id} news={item} />
                        ))}
                    </div>

                    {hasNextPage && (
                        <div className="flex justify-center">
                            <Button
                                text={t("buttons.moreNews")}
                                variant={ButtonVariant.PRIMARY}
                                onClick={handleLoadMore}
                                disabled={isFetchingNextPage}
                            />
                        </div>
                    )}
                </>
            ) : (
                <div className="text-center text-text-300">
                    <p>{t("news.notFound")}</p>
                </div>
            )}
        </section>
    );
}